/**
 * Statistics dashboard (PLAN-Z M8, PLAN.md §6). One code path for guest
 * (IndexedDB) and — from M9 — logged-in (Postgres) data: everything derives
 * from the attempts set via the pure functions in compute.ts.
 *
 * Friendly stat names lead (Average / Typical / Slowest 10%) with the
 * statistical term in a tooltip; a "What do these mean?" expandable carries
 * the plain-language definitions. Headline number: median vs the 75 s/item
 * exam budget.
 */
import { useEffect, useState } from 'react'
import { useAuthUser, type AuthState } from '../auth/auth.ts'
import { loadAttempts, type Attempt } from '../data/local.ts'
import { computeSessions, computeSummary, type BucketStats, type Summary } from './compute.ts'

const BENCHMARK_MS = 75_000

function fmtSec(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`
}
function pct(x: number): string {
  return `${Math.round(x * 100)}%`
}

/** Per-letter accuracy for equations (separates an arithmetic slip from a
 * missed substitution). */
function equationsLetterAccuracy(attempts: Attempt[]): Array<{ letter: string; correct: number; total: number }> {
  const acc: Record<string, { correct: number; total: number }> = {}
  for (const a of attempts) {
    if (a.subtest !== 'equations') continue
    const perLetter = (a.subAnswers.perLetter ?? {}) as Record<string, boolean>
    for (const [letter, ok] of Object.entries(perLetter)) {
      acc[letter] ??= { correct: 0, total: 0 }
      acc[letter].total += 1
      if (ok) acc[letter].correct += 1
    }
  }
  return Object.entries(acc)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, v]) => ({ letter, ...v }))
}

/** Per-blank accuracy for figure sequences (blank 2 projects one frame
 * further — the dashboard shows which blank costs marks). */
function figuresBlankAccuracy(attempts: Attempt[]): Array<{ blank: number; correct: number; total: number }> {
  const acc: Record<number, { correct: number; total: number }> = {}
  for (const a of attempts) {
    if (a.subtest !== 'figures') continue
    const blank = Number((a.subAnswers.blank as number) ?? 0)
    if (!blank) continue
    acc[blank] ??= { correct: 0, total: 0 }
    acc[blank].total += 1
    if (a.correct) acc[blank].correct += 1
  }
  return Object.entries(acc)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([blank, v]) => ({ blank: Number(blank), ...v }))
}

function Stat({ name, term, value }: { name: string; term: string; value: string }) {
  return (
    <span className="stat" title={`${name} — ${term}`}>
      <span className="stat-value">{value}</span> <span className="stat-name">{name}</span>
    </span>
  )
}

function StatsTable({ summary }: { summary: Summary }) {
  const rows: Array<{ key: string; stats: BucketStats }> = []
  for (const [key, stats] of Object.entries(summary.byBucket)) {
    if (stats) rows.push({ key, stats })
  }
  rows.sort((a, b) => a.key.localeCompare(b.key))
  return (
    <table className="stats-table">
      <thead>
        <tr>
          <th scope="col">Bucket</th>
          <th scope="col" title="Scored responses">Answered</th>
          <th scope="col" title="Share of correct responses">Accuracy</th>
          <th scope="col" title="Total time ÷ questions">Average</th>
          <th scope="col" title="Median — half your questions were faster">Typical</th>
          <th scope="col" title="p90 — 9 of 10 questions are faster than this">Slowest 10%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ key, stats }) => (
          <tr key={key}>
            <td>{key}</td>
            <td>{stats.responses}</td>
            <td>{pct(stats.accuracy)}</td>
            <td>{fmtSec(stats.avgMs)}</td>
            <td>{fmtSec(stats.medianMs)}</td>
            <td>{fmtSec(stats.p90Ms)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function StatsDashboard({
  injected,
  auth,
}: {
  injected?: Attempt[]
  auth?: AuthState
} = {}) {
  const liveAuth = useAuthUser()
  const authState = auth ?? liveAuth
  const [attempts, setAttempts] = useState<Attempt[] | null>(injected ?? null)
  const [promptDismissed, setPromptDismissed] = useState(
    () =>
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('register-prompt-dismissed') === 'yes',
  )
  useEffect(() => {
    if (injected === undefined) {
      void loadAttempts().then(setAttempts)
    }
  }, [injected])

  if (attempts === null) {
    return (
      <div className="stats-page">
        <p className="muted">Loading your history…</p>
      </div>
    )
  }
  if (attempts.length === 0) {
    return (
      <div className="stats-page">
        <section className="question-card">
          <h2 className="question-card-title">Statistics</h2>
          <p>No practice history yet. Every answer in a drill or exam block is saved on this
            device and shows up here.</p>
          <a className="question-submit stats-cta" href="/drill">Start a drill</a>
        </section>
      </div>
    )
  }

  const summary = computeSummary(attempts)
  const overall = summary.overall
  const sessions = computeSessions(attempts).slice(-10).reverse()
  const letters = equationsLetterAccuracy(attempts)
  const blanks = figuresBlankAccuracy(attempts)
  const delta = BENCHMARK_MS - overall.medianMs

  // Only guests who can actually register see the prompt — never signed-in users.
  const showRegisterPrompt =
    authState.configured &&
    !authState.loading &&
    authState.user === null &&
    !promptDismissed &&
    attempts.length >= 20

  return (
    <div className="stats-page">
      {showRegisterPrompt && (
        <section className="question-card auth-register-prompt">
          <p>
            You have built up real history on this device. Want it safe across devices?{' '}
            <a href="/register">Create an account</a> — email optional.{' '}
            <button
              type="button"
              className="drill-exit"
              onClick={() => {
                try {
                  localStorage.setItem('register-prompt-dismissed', 'yes')
                } catch {
                  /* private mode — the prompt just reappears */
                }
                setPromptDismissed(true)
              }}
            >
              No thanks
            </button>
          </p>
        </section>
      )}
      <section className="question-card stats-headline">
        <h2 className="question-card-title">Overall</h2>
        <p className="stats-headline-big">
          {pct(overall.accuracy)} accuracy{' '}
          <span className="muted">({overall.correct}/{overall.responses} responses)</span>
        </p>
        <p className="stats-headline-big">
          Typical {fmtSec(overall.medianMs)} —{' '}
          {delta >= 0
            ? `${fmtSec(delta)} ahead of the 75 s budget`
            : `${fmtSec(-delta)} behind the 75 s budget`}
        </p>
        <p className="stats-secondary">
          <Stat name="Average" term="total time ÷ questions; one slow item drags it up" value={fmtSec(overall.avgMs)} />
          {' · '}
          <Stat name="Slowest 10%" term="p90; 9 of 10 questions are faster than this" value={fmtSec(overall.p90Ms)} />
        </p>
        <details className="stats-explain">
          <summary>What do these mean?</summary>
          <ul>
            <li><strong>Average</strong> — total time ÷ questions. One disastrously slow item drags it up, so it can read worse than practice felt.</li>
            <li><strong>Typical</strong> (median) — half your questions were faster, half slower. The honest answer to "how fast am I usually?".</li>
            <li><strong>Slowest 10%</strong> (p90) — 9 of 10 questions are faster than this. This is the number that decides whether you finish the block.</li>
            <li><strong>The budget</strong> — the exam gives 25 minutes for 20 items: 75 s per item on average. A few items past 2 minutes eat the time the rest of the block needs.</li>
          </ul>
        </details>
      </section>

      <section className="question-card">
        <h2 className="question-card-title">By subtest and difficulty</h2>
        <StatsTable summary={summary} />
      </section>

      {blanks.length > 0 && (
        <section className="question-card">
          <h2 className="question-card-title">Figure sequences — per blank</h2>
          <p className="muted">
            The two blanks are scored separately; blank 2 projects one frame further.
          </p>
          <p>
            {blanks.map((b) => (
              <span key={b.blank} className="stat">
                Blank {b.blank}: <strong>{pct(b.correct / b.total)}</strong>{' '}
                <span className="muted">({b.correct}/{b.total})</span>{' '}
              </span>
            ))}
          </p>
        </section>
      )}

      {letters.length > 0 && (
        <section className="question-card">
          <h2 className="question-card-title">Equations — per letter</h2>
          <p className="muted">Separates an arithmetic slip from a missed substitution.</p>
          <p>
            {letters.map((l) => (
              <span key={l.letter} className="stat">
                {l.letter}: <strong>{pct(l.correct / l.total)}</strong>{' '}
                <span className="muted">({l.correct}/{l.total})</span>{' '}
              </span>
            ))}
          </p>
        </section>
      )}

      <section className="question-card">
        <h2 className="question-card-title">Recent sessions</h2>
        <ul className="stats-sessions">
          {sessions.map((s) => (
            <li key={s.sessionId} className="stats-session">
              <span className="stats-session-date">
                {new Date(s.firstAnsweredAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="stats-session-bar" aria-hidden>
                <span style={{ width: `${Math.round(s.accuracy * 100)}%` }} />
              </span>
              <span className="stats-session-numbers">
                {s.responses} responses · {pct(s.accuracy)} · typical {fmtSec(s.medianMs)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
